import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CardType } from 'src/app/core/enums/card-type.enum';
import { Bonus } from 'src/app/core/models/bonus.model';
import { Fee } from 'src/app/core/models/fee.model';
import { LanguageService } from 'src/app/core/services/language.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { LoadingBlockService } from 'src/app/shared/services/loading-block.service';
import { PurchaseService } from 'src/app/shared/services/purchase.service';
import { BuyCardService } from '../../services/buy-card.service';
import { RomaingModalComponent } from '../romaing-modal/romaing-modal.component';

@Component({
	selector: 'app-card-details',
	templateUrl: './card-details.component.html',
	styleUrls: ['./card-details.component.scss'],
	host: {
		class: 'main',
	},
})
export class CardDetailsComponent implements OnInit, OnDestroy {
	feeAmount = 1;
	feeCount = 1;
	totalAmount = 0;
	totalBonusAmount = 0;
	amount = 0;
	CardType = CardType;
	productId!: string;
	fee$!: Observable<any>;
	tagColor!: string;
	bonusId?: string;
	bonuses?: Bonus[];
	bonusSelected?: Bonus;
	fee!: Fee;
	isLoading = false;

	destroy$ = new Subject<void>();

	constructor(
		private readonly router: Router,
		readonly route: ActivatedRoute,
		private readonly dialog: MatDialog,
		readonly purchaseService: PurchaseService,
		readonly buyCardService: BuyCardService,
		readonly errorService: ErrorService,
		readonly loadingBlockService: LoadingBlockService,
		private readonly languageService: LanguageService,
	) {}

	ngOnInit(): void {
		this.productId = this.route.snapshot.paramMap.get('productId') || '';

		this.purchaseService.setPurchase({ productId: this.productId });

		this.loadingBlockService.setIsLoading(true);
		this.isLoading = true;

		//ACTIVATE ONCE ROAMING IS ACTIVE
		// this.buyCardService.getRoaming().subscribe((romaingData) => {
		// 	this.bonusId = romaingData.info.data[0].bonus_id;
		// 	this.buyCardService.getRoamingDetails(romaingData.info.data[0].bonus_id).subscribe((romainData) => {
		// 		this.roamingData = romainData;
		// 		this.isLoading = false;
		// 		this.buyCardService.setRoamingData(romainData);
		// 	});
		// });

		// GET BONUSES
		this.buyCardService.getRoaming().subscribe((bonusData) => {
			this.bonuses = bonusData.info;
			this.loadingBlockService.setIsLoading(false);
			this.isLoading = false;

			const currentPurchase = this.purchaseService.getPurchase();
			if (currentPurchase?.bonusId && !this.bonusSelected) {
				const bonus = this.bonuses?.find((b) => b.bonus_id === currentPurchase?.bonusId);
				if (bonus) {
					this.addBonus(bonus);
				} else {
					// Si no aparece en el listado, eliminar
					this.subtractBonus();
				}
			}
		});
		this.buyCardService.fees$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (data) => {
				if (data.feeSelected) {
					this.fee = data.feeSelected;
					this.amount = Number(data.feeSelected.price);
					this.totalAmount = this.amount;
					this.purchaseService.setPurchase({
						feeSubtitle: data.feeSelected.subtitle,
						feePrice: Number(data.feeSelected.price),
						totalAmount: this.totalAmount,
						remainingAmount: this.totalAmount,
						feeAmount: this.amount,
						feeCount: 1,
					});
				}
			},
			error: () => {},
		});

		this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe({
			next: (value) => (this.tagColor = value.get('tagColor') ?? ''),
			error: () => (this.tagColor = ''),
		});
	}

	valueChange($event: number) {
		this.feeCount = $event;
		this.feeAmount = this.amount * $event;
		this.totalBonusAmount = this.bonusSelected ? Number(this.bonusSelected?.price) * $event : 0;
		this.totalAmount = this.feeAmount + this.totalBonusAmount;
		this.purchaseService.setPurchase({
			feeAmount: this.feeAmount,
			feeCount: this.feeCount,
			totalAmount: this.totalAmount,
			remainingAmount: this.totalAmount,
		});
	}

	openDialog(bonus: Bonus) {
		this.buyCardService.setRoamingData(bonus);
		this.dialog.open(RomaingModalComponent);
	}

	addBonus(bonus: Bonus) {
		// Remove prev bonus
		if (this.bonusSelected) this.subtractBonus();

		this.bonusSelected = bonus;
		this.bonusId = bonus?.bonus_id;

		this.totalBonusAmount = Number(this.bonusSelected?.price) * this.feeCount;
		this.totalAmount += this.totalBonusAmount;
		this.purchaseService.setPurchase({
			roamingTotal: 1 * Number(this.bonusSelected?.price), // count * subtotal
			roamingSubtotal: Number(this.bonusSelected?.price),
			roamingCount: 1,
			totalAmount: this.totalAmount,
			remainingAmount: this.totalAmount,
			isRomaingActivated: true,
			bonusId: this.bonusId,
		});
	}

	subtractBonus() {
		this.bonusSelected = undefined;
		this.bonusId = undefined;

		this.totalAmount -= this.totalBonusAmount;
		this.purchaseService.setPurchase({
			roamingTotal: 0,
			roamingSubtotal: 0,
			roamingCount: 0,
			totalAmount: this.totalAmount,
			remainingAmount: this.totalAmount,
			isRomaingActivated: false,
			bonusId: undefined,
		});
	}

	trackByBonusID(index: any, bonus: Bonus): string {
		return bonus.bonus_id;
	}

	getBonusTitle(bonus: Bonus): string {
		const currentLang = this.languageService.getLanguage();
		if (!currentLang) return '';
		return bonus.translations?.[currentLang]?.title || '';
	}

	getBonusPriceDescription(bonus: Bonus): string {
		const currentLang = this.languageService.getLanguage();
		if (!currentLang) return '';
		return bonus.translations?.[currentLang]?.price_description || '';
	}

	goToContract() {
		this.router.navigate(['buy-card/contract']);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
